import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GRID_SIZE_MOBILE,
  MAX_MOBILE_WIDTH,
  MAX_MOBILE_HEIGHT,
  MAX_TABLET_WIDTH,
  MAX_TABLET_HEIGHT,
  TABLET_PIECE_SIZE,
  DESKTOP_PIECE_SIZE,
} from '../lib/constants';

/**
 * Detect the device type. Returns one of: 'phone' | 'tablet' | 'desktop'.
 *
 * - Phones (iPhone, Android phones): mobile layout with phone-width cap
 * - Tablets (iPad, Android tablets): mobile layout with wider tablet cap so
 *   the puzzle fills more of the screen instead of looking tiny
 * - Desktop / laptop: desktop layout
 */
function detectDeviceType() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';

  const phonePatterns = [
    /iPhone/i,
    /Android.*Mobile/i,
    /webOS/i,
    /iPod/i,
    /BlackBerry/i,
    /IEMobile/i,
    /Opera Mini/i,
    /Windows Phone/i,
  ];
  if (phonePatterns.some((p) => p.test(userAgent))) {
    return 'phone';
  }

  // iPad (including iPadOS reporting Macintosh UA with touch points)
  const isIpad =
    /iPad/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
  // Android tablets (Android UA without "Mobile")
  const isAndroidTablet = /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
  if (isIpad || isAndroidTablet) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Hook to handle viewport layout calculations for the puzzle game.
 * 
 * Device Detection (not viewport-based):
 * - Mobile phones → Mobile layout (always)
 * - Tablets → Mobile layout (always)
 * - Desktop/Laptop → Desktop layout (always, even if window is small)
 */
export function useLayout() {
  // Detect device type once on mount (doesn't change during session)
  const deviceType = useMemo(() => detectDeviceType(), []);
  const isMobileDevice = deviceType === 'phone' || deviceType === 'tablet';
  const isTabletDevice = deviceType === 'tablet';

  const [isLandscape, setIsLandscape] = useState(false);
  const [pieceSize, setPieceSize] = useState(60);
  const currentGridSize = GRID_SIZE_MOBILE; // Always 4x4

  // Calculate sizes based on viewport and device type
  const calculateSizes = useCallback(() => {
    const rawWidth = window.innerWidth;
    const rawHeight = window.innerHeight;

    if (!isMobileDevice) {
      // DESKTOP DEVICE - Always use desktop layout (even if window is small)
      setIsLandscape(false); // Desktop doesn't use landscape mode concept

      // Calculate piece size for desktop - enlarged by 35% for better visibility
      const headerHeight = 120;
      const availableHeight = rawHeight - headerHeight - 80;
      // Increased width allocation for 35% larger pieces
      const availableWidth = Math.min(rawWidth * 0.45, 600);

      const boardPadding = 24;
      const borderWidth = 4;
      const totalGaps = (GRID_SIZE_MOBILE - 1) * 2;
      const availableForPieces = Math.min(availableWidth, availableHeight) - boardPadding - totalGaps - borderWidth;

      // Desktop piece size: 35% larger (base was ~90px, now ~121px max)
      const enlargedPieceSize = Math.floor(DESKTOP_PIECE_SIZE * 1.35);
      const desktopPieceSize = Math.floor(Math.min(
        availableForPieces / GRID_SIZE_MOBILE,
        enlargedPieceSize
      ));

      setPieceSize(Math.max(desktopPieceSize, 50));
    } else {
      // MOBILE DEVICE (phone or tablet)
      // Use wider/taller cap for tablets so the puzzle fills more of an iPad
      // screen instead of sitting in a narrow phone-width column.
      const widthCap = isTabletDevice ? MAX_TABLET_WIDTH : MAX_MOBILE_WIDTH;
      const heightCap = isTabletDevice ? MAX_TABLET_HEIGHT : MAX_MOBILE_HEIGHT;
      const width = Math.min(rawWidth, widthCap);
      const height = Math.min(rawHeight, heightCap);
      const landscape = rawWidth > rawHeight;

      setIsLandscape(landscape);

      let maxBoardWidth, maxBoardHeight;

      if (landscape) {
        // Mobile Landscape: Side-by-side layout
        const headerHeight = 42;
        const availableHeight = height - headerHeight - 4;
        const availableWidth = width - 16;
        const gridGap = 8;

        maxBoardWidth = (availableWidth - gridGap) / 2;
        maxBoardHeight = availableHeight;
      } else {
        // Mobile Portrait: Vertical stack
        const headerHeight = 110;
        const availableHeight = height - headerHeight;
        const safeWidth = Math.min(width, document.documentElement.clientWidth);

        maxBoardWidth = safeWidth - 32;
        maxBoardHeight = (availableHeight - 16) / 2;
      }

      // Calculate piece size
      const boardPadding = landscape ? 12 : 24;
      const borderWidth = 4;
      const totalGaps = (GRID_SIZE_MOBILE - 1) * 2;
      const availableWidthForPieces = maxBoardWidth - boardPadding - totalGaps - borderWidth;
      const availableHeightForPieces = maxBoardHeight - boardPadding - totalGaps - borderWidth;

      // Tablets allow larger piece sizes than phones
      const phoneMaxPiece = landscape ? 70 : 80;
      const maxPieceSize = isTabletDevice ? TABLET_PIECE_SIZE : phoneMaxPiece;

      const newPieceSize = Math.floor(Math.min(
        availableWidthForPieces / GRID_SIZE_MOBILE,
        availableHeightForPieces / GRID_SIZE_MOBILE,
        maxPieceSize
      ));

      setPieceSize(Math.max(newPieceSize, 35));
    }
  }, [isMobileDevice, isTabletDevice]);

  useEffect(() => {
    calculateSizes();
    
    window.addEventListener('resize', calculateSizes);
    
    const handleOrientationChange = () => {
      setTimeout(calculateSizes, 100);
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', calculateSizes);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [calculateSizes]);

  return {
    isMobile: isMobileDevice,
    isTablet: isTabletDevice,
    isLandscape,
    pieceSize,
    currentGridSize
  };
}
